<template>
  <a-modal
    :open="open"
    :confirm-loading="loading"
    :footer="null"
    width="600px"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">상담톡 상세</div>
    </template>

    <div class="modal-content">
      <a-spin :spinning="loading">
        <!-- 회사 정보 -->
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">회사명</span>
            <span class="info-value">{{ counselorData.name || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">신청일</span>
            <span class="info-value">{{ formatDateTime(counselorData.requestedAt) }}</span>
          </div>
        </div>

        <!-- 채널 선택 (복수 채널인 경우) -->
        <div v-if="channelList.length > 1" class="channel-selector">
          <div class="section-title">채널 선택</div>
          <a-radio-group v-model:value="selectedChannelId" @change="onChannelChange">
            <a-radio-button
              v-for="channel in channelList"
              :key="channel.channelId"
              :value="channel.channelId"
            >
              {{ channel.channelId }}
              <a-tag v-if="channel.isDevelopment" color="orange" size="small" class="channel-type-tag">개발</a-tag>
            </a-radio-button>
          </a-radio-group>
        </div>

        <!-- 선택된 채널 정보 -->
        <div class="channel-detail-section">
          <div class="section-title">채널 정보</div>
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">채널ID</span>
              <span class="info-value channel-id">{{ selectedChannel?.channelId || counselorData.channelId || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">채널유형</span>
              <span class="info-value">
                <a-tag v-if="selectedChannel?.isDevelopment || counselorData.isDeveloperChannel" color="orange">개발</a-tag>
                <a-tag v-else color="blue">운영</a-tag>
              </span>
            </div>
            <div class="info-row admin-row">
              <span class="info-label">관리자 연락처</span>
              <div class="info-value admin-list">
                <template v-if="adminList.length > 0">
                  <div v-for="(phone, idx) in adminList" :key="phone" class="admin-item">
                    <span class="admin-phone">{{ formatPhone(phone) }}</span>
                    <a-popconfirm
                      title="이 관리자를 삭제하시겠습니까?"
                      ok-text="삭제"
                      cancel-text="취소"
                      @confirm="removeAdmin(idx)"
                    >
                      <delete-outlined class="delete-icon" />
                    </a-popconfirm>
                  </div>
                </template>
                <template v-else>
                  <span class="no-admin">등록된 관리자가 없습니다.</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <a-divider />

        <!-- 수정 가능한 폼 영역 -->
        <a-form
          ref="formRef"
          :model="formState"
          :rules="formRules"
          layout="vertical"
        >
          <a-form-item label="발신프로필키" name="senderKey">
            <a-input
              v-model:value="formState.senderKey"
              placeholder="발신프로필키를 입력하세요"
              :disabled="formState.status === 'disconnected'"
            />
            <div class="form-hint">
              * 승인(연결됨) 상태로 변경하려면 발신프로필키 입력이 필수입니다.
            </div>
          </a-form-item>

          <a-form-item label="수신 URL" name="receiveUrl">
            <a-input
              v-model:value="formState.receiveUrl"
              placeholder="https://counsel-api.certi.life/kakaotalk"
              :disabled="!formState.syncDkt || formState.status === 'disconnected'"
            />
            <div class="form-hint">
              * 상담톡 수신 URL입니다. /kakaotalk 경로는 서버에서 자동 보정됩니다.
            </div>
          </a-form-item>

          <a-form-item>
            <a-checkbox v-model:checked="formState.syncDkt">
              DKT 수신 URL 동기화
            </a-checkbox>
            <div class="form-hint">
              * 이미 연결된 채널의 수신 URL을 DKT에 반영합니다. 체크 해제 시 내부 상태만 저장합니다.
            </div>
            <div class="form-hint form-hint--warn">
              * 상담톡 등록/해지는 DKT가 API를 폐지하여 자동 처리되지 않습니다. DKT 포털에서 직접
              처리한 뒤 이 화면에서 상태를 반영해주세요.
            </div>
          </a-form-item>

          <a-form-item label="상태" name="status">
            <a-select
              v-model:value="formState.status"
              :options="statusOptions"
              placeholder="상태를 선택하세요"
            />
          </a-form-item>
        </a-form>

        <!-- 푸터 버튼 영역 -->
        <div class="modal-footer">
          <a-button @click="handleCancel">취소</a-button>
          <a-button
            type="primary"
            :loading="loading"
            @click="handleSubmit"
            class="yellow-button"
          >
            저장
          </a-button>
        </div>
      </a-spin>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, watch, computed } from 'vue';
import { message } from 'ant-design-vue';
import { DeleteOutlined } from '@ant-design/icons-vue';
import { COUNSELOR_STATUS, COUNSELOR_STATUS_OPTIONS } from '../constants';
import CounselorApi from '../api';

export default {
  name: 'CounselorModal',
  components: {
    DeleteOutlined,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    counselorData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    const formRef = ref(null);
    const statusOptions = COUNSELOR_STATUS_OPTIONS;

    // 선택된 채널 ID
    const selectedChannelId = ref('');

    // 폼 상태
    const formState = reactive({
      senderKey: '',
      receiveUrl: '',
      syncDkt: true,
      status: '',
    });

    // 관리자 목록 (수정 가능)
    const adminList = ref([]);

    // 유효성 검사 규칙
    const formRules = {
      status: [{ required: true, message: '상태를 선택해주세요' }],
    };

    // 채널 목록 (kakaoChannels 또는 단일 채널)
    const channelList = computed(() => {
      if (props.counselorData.kakaoChannels?.length > 0) {
        return props.counselorData.kakaoChannels;
      }
      // 단일 채널 구조인 경우
      if (props.counselorData.channelId) {
        // admins 배열 생성 (다양한 구조 지원)
        let admins = [];
        if (props.counselorData.admins?.length > 0) {
          admins = props.counselorData.admins;
        } else if (props.counselorData.adminPhones?.length > 0) {
          admins = props.counselorData.adminPhones.map(phone => ({ phone }));
        } else if (props.counselorData.adminPhone) {
          admins = [{ phone: props.counselorData.adminPhone }];
        }

        return [{
          channelId: props.counselorData.channelId,
          isDevelopment: props.counselorData.isDeveloperChannel,
          senderKey: props.counselorData.senderKey,
          status: props.counselorData.status,
          admins,
        }];
      }
      return [];
    });

    // 선택된 채널 정보
    const selectedChannel = computed(() => {
      return channelList.value.find(ch => ch.channelId === selectedChannelId.value) || null;
    });

    // 채널에서 관리자 전화번호 추출 (다양한 구조 지원)
    const extractAdminPhonesFromChannel = (channel) => {
      if (!channel) return [];

      // 구조 1: channel.admins 배열 (객체 또는 문자열)
      if (channel.admins?.length > 0) {
        return channel.admins.map(a => a.phone || a);
      }

      // 구조 2: channel.adminPhone 단일 값
      if (channel.adminPhone) {
        return [channel.adminPhone];
      }

      // 구조 3: channel.adminPhones 배열
      if (channel.adminPhones?.length > 0) {
        return [...channel.adminPhones];
      }

      return [];
    };

    // 채널 변경 시 폼 업데이트
    const onChannelChange = () => {
      const channel = selectedChannel.value;
      if (channel) {
        formState.senderKey = channel.senderKey || '';
        formState.receiveUrl = channel.receiveUrl || props.counselorData.receiveUrl || '';
        formState.syncDkt = true;
        formState.status = channel.status || '';
        adminList.value = extractAdminPhonesFromChannel(channel);
      }
    };

    // 관리자 삭제
    const removeAdmin = async (index) => {
      const phone = adminList.value[index];
      const companyId = props.counselorData._id;
      const channelId = selectedChannelId.value;

      try {
        await CounselorApi.deleteAdmin(companyId, channelId, phone);
        adminList.value.splice(index, 1);
        message.success('관리자가 삭제되었습니다.');
      } catch (error) {
        console.error('Delete admin failed:', error);
        message.error('관리자 삭제에 실패했습니다.');
      }
    };

    // counselorData가 변경될 때 초기화
    watch(
      () => props.counselorData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          // 첫 번째 채널 선택
          if (newData.kakaoChannels?.length > 0) {
            selectedChannelId.value = newData.kakaoChannels[0].channelId;
            const firstChannel = newData.kakaoChannels[0];
            formState.senderKey = firstChannel.senderKey || '';
            formState.receiveUrl = firstChannel.receiveUrl || newData.receiveUrl || '';
            formState.syncDkt = true;
            formState.status = firstChannel.status || '';
            adminList.value = extractAdminPhonesFromChannel(firstChannel);
          } else if (newData.channelId) {
            selectedChannelId.value = newData.channelId;
            formState.senderKey = newData.senderKey || '';
            formState.receiveUrl = newData.receiveUrl || '';
            formState.syncDkt = true;
            formState.status = newData.status || '';
            // 다양한 관리자 구조 지원
            adminList.value = extractAdminPhonesFromChannel(newData);
          }
        }
      },
      { immediate: true, deep: true }
    );

    // 전화번호 포맷팅
    const formatPhone = (phone) => {
      if (!phone) return '-';
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    };

    // 날짜/시간 포맷팅
    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    // 제출 핸들러
    const handleSubmit = async () => {
      // connected로 변경 시 senderKey 필수 검증
      if (formState.status === COUNSELOR_STATUS.CONNECTED && !formState.senderKey) {
        message.warning('승인(연결됨) 상태로 변경하려면 발신프로필키를 입력해주세요.');
        return;
      }

      if (
        formState.status === COUNSELOR_STATUS.CONNECTED &&
        formState.syncDkt &&
        !formState.receiveUrl
      ) {
        message.warning('DKT에 등록하려면 수신 URL을 입력해주세요.');
        return;
      }

      try {
        await formRef.value?.validate();
        emit('ok', {
          channelId: selectedChannelId.value,
          senderKey: formState.senderKey,
          receiveUrl: formState.receiveUrl,
          syncDkt: formState.syncDkt,
          status: formState.status,
        });
      } catch (error) {
        console.error('Validation failed:', error);
      }
    };

    // 취소 핸들러
    const handleCancel = () => {
      emit('cancel');
    };

    return {
      formRef,
      formState,
      formRules,
      statusOptions,
      selectedChannelId,
      selectedChannel,
      channelList,
      adminList,
      onChannelChange,
      removeAdmin,
      formatPhone,
      formatDateTime,
      handleSubmit,
      handleCancel,
    };
  },
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #000;
}

.modal-content {
  padding: 8px 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.info-section {
  background: #fafafa;
  padding: 16px;
  border-radius: 8px;
}

.info-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.info-label {
  width: 120px;
  color: #666;
  font-size: 14px;
  flex-shrink: 0;
}

.info-value {
  flex: 1;
  color: #000;
  font-size: 14px;
  font-weight: 500;
}

.channel-id {
  color: #1890ff;
  font-family: monospace;
}

.channel-selector {
  margin-top: 16px;
  margin-bottom: 16px;
}

.channel-type-tag {
  margin-left: 4px;
  font-size: 10px;
}

.channel-detail-section {
  margin-top: 16px;
}

.admin-row {
  align-items: flex-start;
}

.admin-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.admin-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.admin-phone {
  font-size: 14px;
  color: #333;
}

.delete-icon {
  color: #ff4d4f;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: #ff7875;
  }
}

.no-admin {
  color: #999;
  font-size: 13px;
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

/* DKT가 등록/해지 API를 폐지하여 수동 처리가 필요함을 알리는 경고 */
.form-hint--warn {
  color: #d46b08;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.yellow-button {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82d !important;
    border-color: #e6b82d !important;
  }
}

:deep(.ant-divider) {
  margin: 16px 0;
}

:deep(.ant-form-item-label > label) {
  font-weight: 500;
}

:deep(.ant-radio-button-wrapper) {
  margin-right: 8px;
  margin-bottom: 8px;
}
</style>
